import { deviceOS, routeConstants } from "@utils/constants";
import { checkSegments } from "./util";
import { isEmailWinback } from "./email";
import { isUS, isIntl } from "./locale";
import getStore, { CoreReduxState } from "../redux/store";
import { getMeristemContext, getSubdivision } from "../meristemContext";
import { isAppBaseRoute } from "./in-app";

export interface SegmentFunction {
  (state?: CoreReduxState): boolean;
  load?: (store?: CoreReduxState) => Promise<void>;
}

// Baseline/control routes
export const isCore = checkSegments({
  oneOf: [
    routeConstants.exsf01,
    routeConstants.exsf01d,
    routeConstants.awsem,
    routeConstants.direct,
    routeConstants.appEmail,
  ],
});

export const isCoreDirect = checkSegments({
  oneOf: [routeConstants.direct, routeConstants.baseline],
});

export const isDirect = checkSegments({
  oneOf: [routeConstants.direct],
});

export const isCorePaid = checkSegments({
  oneOf: [routeConstants.exsf01, routeConstants.exsf01d, routeConstants.awsem],
});

export const isCorePaidWithoutAddWordsSEM = checkSegments({
  oneOf: [routeConstants.exsf01, routeConstants.exsf01d],
});

export const isAffiliateWithoutTrial = checkSegments({
  oneOf: [
    routeConstants.affiliateNoTrial,
    routeConstants.affiliateNoTrialPromo,
  ],
});

export const isAffiliateWithTrial = checkSegments({
  oneOf: [routeConstants.affiliateTrialNPB],
});

export const isAffiliate = checkSegments({
  oneOf: [isAffiliateWithoutTrial, isAffiliateWithTrial],
});

export const isReferrals = checkSegments({
  oneOf: [routeConstants.referrals],
});

export const isWinback = checkSegments({
  oneOf: [routeConstants.winback],
});

export const isWebWinback = checkSegments({
  oneOf: [isEmailWinback, isWinback],
});

// Set of routes that share the same buyflow survey experience as Core
export const isMantle = checkSegments({
  oneOf: [isCore, isAffiliate, isAppBaseRoute, isReferrals, isWebWinback],
});

export const isUSMantle = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle],
});

export const isIntlMantle = checkSegments({
  allOf: [isIntl],
  oneOf: [isMantle],
});

export const isAdWordsSEM = checkSegments({
  oneOf: [routeConstants.awsem],
});
export const isUSAdWordsSEM = checkSegments({
  allOf: [isUS],
  oneOf: [isAdWordsSEM],
});

export const isHM = checkSegments({
  oneOf: [
    routeConstants.hmDirect,
    routeConstants.hmExsf01,
    routeConstants.hmExsf01d,
    routeConstants.hmPaid,
    routeConstants.hmAwsem,
    routeConstants.hmEmail,
    routeConstants.hmReferrals,
  ],
});

// Checks if the user is on the diabetes prevention program route
export const isDPP = checkSegments({
  oneOf: [routeConstants.ndpp],
});

export const isPreSignup = () =>
  [
    "landing",
    "main-survey",
    "payment",
    "payment-survey",
    "payment-survey-referrals",
    "payment-survey-nm-bundle",
    "payment-results",
    "payment-checkout",
  ].includes(getMeristemContext().context_type);

export * from "./locale";
export * from "./demographics";
export * from "./email";
export * from "./in-app";

export const isMobile = () => {
  return !!getMeristemContext().device_is_mobile;
};

export const isIOS = () => {
  return getMeristemContext().device_os === deviceOS.iOS;
};

// Old style
/**
 * Verifies if current user is on winback route and is from US
 */
export const isUSWinback = checkSegments({
  allOf: [isUS],
  oneOf: [isWinback],
});

export const isCalifornia = (state = getStore().getState()) => {
  const { serverContext } = state;
  return (
    (isUS() && getSubdivision() === "CA") ||
    serverContext?.billing_state === "California"
  );
};
